import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormStateService } from '../../../services/formly/form-state.service';
import { BehaviorSubject, distinctUntilChanged, Subscription } from 'rxjs';
import { FieldTypeConfig, FormlyFieldProps } from '@ngx-formly/core';
import { FieldType } from '@ngx-formly/bootstrap/form-field';
import { alphaNanoIdUtil } from '../../../../utils/utils/alpha-nano-id.util';

export interface GlobalErrorFieldConfig extends FormlyFieldProps {
  errorKey?: string | number | (string | number)[];
  layout?: string;
  nonConfigurable?: boolean;
  resetErrorOnFields?: string[];
  [key: string]: any;
}

@Component({
  standalone: false,
  selector: 'irembogov-formly-global-error',
  templateUrl: './irembogov-formly-global-error.component.html',
  styleUrls: ['./irembogov-formly-global-error.component.css'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class IrembogovFormlyGlobalErrorComponent
  extends FieldType<FieldTypeConfig<GlobalErrorFieldConfig>>
  implements OnInit, OnDestroy
{
  private readonly subscription = new Subscription();
  error$ = new BehaviorSubject<any>(null);

  constructor(private readonly formStateService: FormStateService) {
    super();
    this.defaultProps();
  }

  ngOnInit() {
    this.subscription.add(
      this.formStateService
        .getFormlyFormStateSubject()
        .pipe(
          distinctUntilChanged((prev, curr) => {
            // Only update if the error matches our errorKey
            const prevError = prev?.globalError;
            const currError = curr?.globalError;

            if (this.field.props?.['errorKey']) {
              // If errorKey is specified, only show errors for that key
              return (
                prevError?.field === this.field.props?.['errorKey'] &&
                currError?.field === this.field.props?.['errorKey'] &&
                JSON.stringify(prevError) === JSON.stringify(currError)
              );
            }

            // If no errorKey specified, show all errors
            return JSON.stringify(prevError) === JSON.stringify(currError);
          })
        )
        .subscribe(state => {
          let error = null;
          const errorKey = this.field.props?.['errorKey'] as string;

          if (errorKey) {
            // Check field-specific errors first (new structure)
            if (state?.globalErrors && state.globalErrors[errorKey]) {
              error = state.globalErrors[errorKey];
            } else if (state?.globalError?.field === errorKey) {
              // Fallback to old structure for backward compatibility
              error = state.globalError;
            }
          } else {
            // Show the most recent error from either structure
            error = state?.globalError || null;
          }

          this.error$.next(error);
        })
    );

    this.setupResetErrorOnFieldsListener();
  }

  private setupResetErrorOnFieldsListener(): void {
    const resetFields: string[] =
      this.field.props?.['resetErrorOnFields'] ?? [];

    if (!resetFields.length) {
      return;
    }

    this.subscription.add(
      this.field.options?.fieldChanges
        ?.pipe(
          distinctUntilChanged(
            (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
          )
        )
        .subscribe(changes => {
          if (resetFields.includes(changes.field.key as string)) {
            this.clearError();
          }
        })
    );
  }

  clearError() {
    const errorKey = this.field.props?.['errorKey'];
    if (errorKey) {
      // Create a mock field config for the specific error key
      const fieldConfig = { key: errorKey } as any;
      this.formStateService.clearGlobalError(fieldConfig);
    } else {
      this.formStateService.clearGlobalError();
    }
  }

  defaultProps() {
    if (this.field) {
      // Ensure props object exists
      this.field.props = this.field.props || {};

      // Set mandatory properties
      this.field.props = {
        ...this.field.props,
        nonConfigurable: true,
        hideHeader: true,
        hideLabel: true,
        // Ensure errorKey exists
        errorKey:
          this.field.props?.['errorKey'] ||
          this.field.key ||
          `InputKey_${alphaNanoIdUtil(5)}`,
      };
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
